import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactNode } from 'react';
import { Box, Grid, IconButton, Tab, Tabs } from '@mui/material';
import {
  DeleteOutline, Numbers, DonutLarge, BarChart, StackedBarChart, ShowChart, TableRows, Notes, DashboardCustomizeOutlined,
} from '@mui/icons-material';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { LoadingPage } from '../../loading-page';
import { InformationBox } from '../../../presentation-components/miscellaneous-components/information-box';
import { AnalyticReportCard } from '../../../presentation-components/analytics-components/reports/analytic-report-card';
import { useHasAReportPeriod } from '../../../../hooks/use-has-a-report-period';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import DashboardService from '../../../../shared/services/dashboard/dashboard.service';
import DashboardElementService from '../../../../shared/services/dashboard-element/dashboard-element.service';
import { IDashboard, IDashboardElement } from '../../../../shared/services/dashboard/dashboard.model';
import { DashboardDialog } from '../../../dialogs/dashboard/dashboard-dialog';
import { DashboardElementDialog } from '../../../dialogs/dashboard/dashboard-element-dialog';

const dashboardService = new DashboardService();
const dashboardElementService = new DashboardElementService();

// A distinct icon per element type, so a library element's card conveys what it is at a glance.
const ELEMENT_TYPE_ICONS: Record<string, ReactNode> = {
  counter: <Numbers sx={{ fontSize: '3rem' }} />,
  doughnut: <DonutLarge sx={{ fontSize: '3rem' }} />,
  bar: <BarChart sx={{ fontSize: '3rem' }} />,
  stacked: <StackedBarChart sx={{ fontSize: '3rem' }} />,
  line: <ShowChart sx={{ fontSize: '3rem' }} />,
  table: <TableRows sx={{ fontSize: '3rem' }} />,
  text: <Notes sx={{ fontSize: '3rem' }} />,
};

const elementTypeIcon = (type: string): ReactNode => ELEMENT_TYPE_ICONS[type] || <Numbers sx={{ fontSize: '3rem' }} />;

export const CustomDashboardsPage = () => {
  const navigate = useNavigate();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const hasAReportPeriod = useHasAReportPeriod();

  const [tab, setTab] = useState(0);
  const [dashboards, setDashboards] = useState<IDashboard[]>([]);
  const [elements, setElements] = useState<IDashboardElement[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
  const [elementDialogOpen, setElementDialogOpen] = useState(false);
  const [editingElement, setEditingElement] = useState<IDashboardElement | undefined>(undefined);

  const fetchData = () => {
    setIsLoading(true);
    Promise.all([dashboardService.getAll(), dashboardElementService.getAll()])
      .then(([dashboardsResponse, elementsResponse]) => {
        setDashboards(dashboardsResponse);
        setElements(elementsResponse);
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDeleteDashboard = (dashboard: IDashboard) => {
    userNotificationsContext.openDialog({
      active: true, title: 'Confirmation',
      message: `Are you sure you want to delete "${dashboard.name}"? This action cannot be undone.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel',
    }).then((confirmed) => {
      if (!confirmed) return;
      dashboardService.remove(dashboard.id)
        .then(() => { userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' }); fetchData(); })
        .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: '#70787A' }));
    }).catch((err) => console.log(err));
  };

  const handleDeleteElement = (element: IDashboardElement) => {
    userNotificationsContext.openDialog({
      active: true, title: 'Confirmation',
      message: `Are you sure you want to delete "${element.name}"? It will be removed from any dashboards that use it.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel',
    }).then((confirmed) => {
      if (!confirmed) return;
      dashboardElementService.remove(element.id)
        .then(() => { userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' }); fetchData(); })
        .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: '#70787A' }));
    }).catch((err) => console.log(err));
  };

  const openNewElement = () => { setEditingElement(undefined); setElementDialogOpen(true); };
  const openEditElement = (element: IDashboardElement) => { setEditingElement(element); setElementDialogOpen(true); };

  return (
    <PageOuterContainer>
      <PageHeading
        title="Custom Dashboards"
        includeActionButton={hasAReportPeriod === true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => (tab === 0 ? setDashboardDialogOpen(true) : openNewElement())}
        actionButtonText={tab === 0 ? 'New Dashboard' : 'New Element'}
        hideShowActionButton={!hasAReportPeriod}
        hasTabs={false}
      />

      {hasAReportPeriod !== null && hasAReportPeriod === false && (
        <Box sx={{ mt: 2 }}>
          <InformationBox
            text="You need to create a report period before you can use custom dashboards."
            icon={IconName.ErrorOutline}
          />
        </Box>
      )}

      {hasAReportPeriod && (
        <>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Dashboards" />
            <Tab label="Elements" />
          </Tabs>

          <LoadingPage pageReady={!isLoading}>
            {tab === 0 && (
              <Grid container spacing={3}>
                {dashboards.map((dashboard) => (
                  <Grid key={dashboard.id} item xl={4} lg={4} md={6} sm={12} xs={12}>
                    <Box sx={{ position: 'relative' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDeleteDashboard(dashboard); }}
                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                      <AnalyticReportCard
                        name={dashboard.name}
                        iconNode={<DashboardCustomizeOutlined />}
                        onClick={() => navigate(`/analytics/custom-dashboards/${dashboard.id}`)}
                      />
                    </Box>
                  </Grid>
                ))}
                {/* Must be a Grid item: a plain child of a spacing container sits at the
                    container's negative-margin edge, so the box juts left of the page title. */}
                {dashboards.length === 0 && (
                  <Grid item xs={12}>
                    <InformationBox text="You do not have any custom dashboards yet." icon={IconName.ErrorOutline} />
                  </Grid>
                )}
              </Grid>
            )}

            {tab === 1 && (
              <Grid container spacing={3}>
                {elements.map((element) => (
                  <Grid key={element.id} item xl={4} lg={4} md={6} sm={12} xs={12}>
                    <Box sx={{ position: 'relative' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDeleteElement(element); }}
                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                      <AnalyticReportCard name={element.name} iconNode={elementTypeIcon(element.type)} onClick={() => openEditElement(element)} />
                    </Box>
                  </Grid>
                ))}
                {elements.length === 0 && (
                  <Grid item xs={12}>
                    <InformationBox
                      text="You do not have any dashboard elements yet. Create elements here, then place them on a dashboard."
                      icon={IconName.ErrorOutline}
                    />
                  </Grid>
                )}
              </Grid>
            )}
          </LoadingPage>
        </>
      )}

      <DashboardDialog
        open={dashboardDialogOpen}
        onClose={() => setDashboardDialogOpen(false)}
        onSuccess={(created) => navigate(`/analytics/custom-dashboards/${created.id}/edit`)}
      />
      <DashboardElementDialog
        open={elementDialogOpen}
        onClose={() => setElementDialogOpen(false)}
        onSuccess={fetchData}
        element={editingElement}
      />
    </PageOuterContainer>
  );
};
